import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, Search, Filter, Plus, Calendar, Baby, MessageCircle, Sparkles } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { infantilAPI } from '../services/api';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35 } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.04 } } };

const FIELD_COLORS = { 'bncc-ei-01': '#8E52AB', 'bncc-ei-02': '#F59E0B', 'bncc-ei-03': '#EC4899', 'bncc-ei-04': '#3B82F6', 'bncc-ei-05': '#10B981' };
const STRENGTH_COLORS = { 'indício': '#94A3B8', 'evidência': '#3B82F6', 'evidência forte': '#8E52AB', 'padrão consolidado': '#2BB673' };

export default function ObservationsListPage() {
  const navigate = useNavigate();
  const [observations, setObservations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [ref, setRef] = useState(null);

  useEffect(() => {
    infantilAPI.reference().then(setRef);
    infantilAPI.observations.list({ limit: 50 }).then(d => {
      setObservations(d.items || []);
      setTotal(d.total || 0);
      setLoading(false);
    });
  }, []);

  const filtered = observations.filter(o =>
    (o.title || '').toLowerCase().includes(search.toLowerCase()) ||
    (o.observation_text || '').toLowerCase().includes(search.toLowerCase()) ||
    (o.context || '').toLowerCase().includes(search.toLowerCase())
  );

  const fieldName = (id) => ref?.bncc_fields?.find(f => f.id === id)?.name || '';
  const dimName = (id) => ref?.dimensions?.find(d => d.id === id)?.name || '';

  return (
    <motion.div className="space-y-6" initial="hidden" animate="visible" variants={stagger} data-testid="observations-list">
      {/* Header */}
      <motion.div variants={fadeUp} className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Eye className="h-5 w-5 text-[#64327D]" /> Observações Pedagógicas
          </h1>
          <p className="text-sm text-slate-500 mt-1">{total} registros encontrados</p>
        </div>
        <button onClick={() => navigate('/v2/infantil/novo-registro')} data-testid="new-observation-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-medium text-sm transition-all hover:shadow-lg"
          style={{ background: 'linear-gradient(135deg, #2BB673, #1a8f5a)' }}>
          <Plus className="h-4 w-4" /> Novo Registro
        </button>
      </motion.div>

      {/* Search */}
      <motion.div variants={fadeUp} className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input placeholder="Buscar por título, texto, contexto..."
          className="pl-10 rounded-xl bg-white" value={search} onChange={e => setSearch(e.target.value)} />
      </motion.div>

      {/* List */}
      {loading ? (
        <div className="space-y-3 animate-pulse">
          {[...Array(5)].map((_, i) => <div key={i} className="h-24 rounded-2xl bg-slate-100" />)}
        </div>
      ) : filtered.length === 0 ? (
        <motion.div variants={fadeUp} className="text-center py-16 bg-white rounded-2xl border border-slate-100">
          <Eye className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">Nenhuma observação registrada</p>
          <p className="text-sm text-slate-400 mt-1">Clique em "Novo Registro" para começar</p>
        </motion.div>
      ) : (
        <div className="space-y-3">
          {filtered.map((obs, i) => (
            <motion.div key={obs.id} variants={fadeUp}
              className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer group"
              onClick={() => {}}>
              <div className="flex items-start gap-4">
                {/* Color bar */}
                <div className="w-1.5 h-16 rounded-full flex-shrink-0 mt-1"
                  style={{ backgroundColor: FIELD_COLORS[obs.bncc_field_id] || '#94A3B8' }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-bold text-slate-800 truncate">{obs.title}</h3>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                      style={{ backgroundColor: (STRENGTH_COLORS[obs.evidence_strength] || '#94A3B8') + '18', color: STRENGTH_COLORS[obs.evidence_strength] || '#94A3B8' }}>
                      {obs.evidence_strength}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 line-clamp-2 mb-2">{obs.observation_text}</p>
                  {obs.child_speech && (
                    <p className="text-xs italic text-[#64327D] bg-[#5B3B8C08] px-3 py-1.5 rounded-lg mb-2 flex items-start gap-1.5">
                      <MessageCircle className="h-3 w-3 mt-0.5 flex-shrink-0" /> "{obs.child_speech}"
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#DDF7EC] text-[#2BB673] font-medium">{obs.context}</span>
                    {obs.bncc_field_id && <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: (FIELD_COLORS[obs.bncc_field_id] || '#94A3B8') + '15', color: FIELD_COLORS[obs.bncc_field_id] }}>{fieldName(obs.bncc_field_id)}</span>}
                    {obs.dimension_id && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">{dimName(obs.dimension_id)}</span>}
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50 text-slate-400">{obs.date}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50 text-slate-400">{obs.evidence_nature}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
}
